/**
 * Calls the API and unwraps the `{ data }` / `{ error }` envelope described in
 * docs/API_ENDPOINTS.md, sending the signed-in token.
 *
 * Two targets, chosen at build time:
 * - our API (default): `/cpaas/...`, proxied to `npm run server` in dev;
 * - the backend's agency API (`VITE_API_MODE=agency`):
 *   https://api.twiva.co.ke/api/v1/cpaas/agency/..., through agencyAdapter.ts,
 *   which translates the screens' calls to that API.
 */

import { ApiError } from './apiError';
import { agencyRequest } from './agencyAdapter';
import { trackLoading } from './loading';

const TOKEN_KEY = 'twiva.authToken';
/** "agency" = the backend's agency API; anything else = our API. */
export const API_MODE = import.meta.env.VITE_API_MODE === 'agency' ? 'agency' : 'local';
/** VITE_API_URL overrides the host: same origin for our API, api.twiva.co.ke for the agency API. */
const API_BASE =
  API_MODE === 'agency'
    ? `${import.meta.env.VITE_API_URL || 'https://api.twiva.co.ke'}/api/v1/cpaas/agency`
    : `${import.meta.env.VITE_API_URL ?? ''}/cpaas`;

/**
 * Pages anyone can open without signing in (a campaign's join page): our API's `/cpaas`, or the
 * backend's public CPaaS routes (`/api/v1/cpaas/...`, outside the agency API).
 */
const PUBLIC_API_BASE =
  API_MODE === 'agency'
    ? `${import.meta.env.VITE_API_URL || 'https://api.twiva.co.ke'}/api/v1/cpaas`
    : `${import.meta.env.VITE_API_URL ?? ''}/cpaas`;

/** Fired when the API rejects the token, so the app can send the user to login. */
export const AUTH_EXPIRED_EVENT = 'twiva:auth-expired';

export { ApiError };

type RequestOptions = NonNullable<Parameters<typeof fetch>[1]>;

export interface ListMeta {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage unavailable (private mode) — the session just won't survive a reload.
  }
}

/** Every screen's request goes through here, so it also drives the global progress bar. */
function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
  const pending = API_MODE === 'agency' ? agencyRequest(path, init, send) : send(path, init);
  return trackLoading(pending) as Promise<T>;
}

/** One HTTP call to the configured API; returns the parsed JSON body or throws ApiError. */
async function send(path: string, init: RequestOptions = {}): Promise<unknown> {
  const token = getToken();
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(
      0,
      'network_error',
      API_MODE === 'agency' ? 'Cannot reach the Twiva API. Check your connection.' : 'Cannot reach the API. Is `npm run server` running?',
    );
  }

  const body = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const error = body?.error ?? {};
    if (response.status === 401 && token) {
      setToken(null);
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    // Laravel's validation errors arrive as { message, errors } rather than { error }.
    throw new ApiError(response.status, error.code ?? 'unknown', error.message ?? body?.message ?? `Request failed (${response.status}).`);
  }
  return body;
}

/** GET/POST/… a single resource and return its `data`. */
export async function api<T>(path: string, init?: RequestOptions): Promise<T> {
  const body = await request<{ data: T }>(path, init);
  return body?.data as T;
}

/** Call a public endpoint (no sign-in token, no agency adapter) and return its `data`. */
export async function publicApi<T>(path: string, init: RequestOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${PUBLIC_API_BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...init.headers },
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Cannot reach Twiva. Check your connection and try again.');
  }
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(response.status, body?.error?.code ?? 'unknown', body?.error?.message ?? body?.message ?? `Request failed (${response.status}).`);
  }
  return body?.data as T;
}

/** GET a paginated list and return `data` plus `meta`. */
export function apiList<T>(path: string): Promise<{ data: T[]; meta: ListMeta }> {
  return request<{ data: T[]; meta: ListMeta }>(path);
}
